import type { Job, JobMetadataCloseoutSignoff } from "@/data/types";

export interface CloseoutSignoffValidation {
  valid: boolean;
  blockers: string[];
}

export function normalizeCloseoutSignaturePaths(
  value: JobMetadataCloseoutSignoff["signaturePaths"] | undefined,
): Array<Array<{ x: number; y: number }>> {
  if (!Array.isArray(value)) return [];
  return value
    .map((stroke) => {
      if (!Array.isArray(stroke)) return [];
      return stroke.filter(
        (point): point is { x: number; y: number } =>
          !!point && Number.isFinite(point.x) && Number.isFinite(point.y),
      );
    })
    .filter((stroke) => stroke.length > 0);
}

export function getCloseoutSignoff(job: Pick<Job, "closeoutSignoff">): JobMetadataCloseoutSignoff | null {
  return job.closeoutSignoff ?? null;
}

export function validateCloseoutSignoff(
  signoff: JobMetadataCloseoutSignoff | null | undefined,
): CloseoutSignoffValidation {
  const blockers: string[] = [];
  if (!signoff) return { valid: false, blockers: ["Homeowner closeout sign-off is missing"] };

  const signerType = String(signoff.signerType ?? "").trim();
  const signerName = String(signoff.signerName ?? "").trim();
  const signatureDataUrl = String(signoff.signatureDataUrl ?? "").trim();
  const signaturePaths = normalizeCloseoutSignaturePaths(signoff.signaturePaths);

  if (!signerType) blockers.push("Signer type is missing");
  if (!signerName) blockers.push("Signer name is missing");
  if (signoff.approved !== true) blockers.push("Homeowner sign-off has not been approved");
  if (!signatureDataUrl) blockers.push("Rendered signature snapshot is missing");
  if (signaturePaths.length === 0) blockers.push("Signature stroke data is missing");

  return { valid: blockers.length === 0, blockers };
}

export function hasValidCloseoutSignoff(job: Pick<Job, "closeoutSignoff">): boolean {
  return validateCloseoutSignoff(job.closeoutSignoff).valid;
}
